import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Library as LibraryIcon, Loader2, Palette, Search, Crown, X, ArrowRight, Sparkles } from "lucide-react";

const STYLE_SWATCH = {
  editorial: "linear-gradient(135deg, #d4885a 0%, #4f7942 100%)",
  isometric: "linear-gradient(135deg, #4f7d8a 0%, #d4a537 100%)",
  watercolor: "linear-gradient(135deg, #a5c99a 0%, #e8a595 100%)",
  "retro-diagram": "linear-gradient(135deg, #b8875e 0%, #6a4a2f 100%)",
};

const STYLE_LABEL = {
  editorial: "Editorial",
  isometric: "Isometric",
  watercolor: "Watercolor",
  "retro-diagram": "Retro Diagram",
};

export default function Library() {
  const [items, setItems] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(null); // {plan, reason} when 402
  const [styleFilter, setStyleFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const limit = 60;

  const load = useCallback(async (nextOffset = 0) => {
    setLoading(true);
    try {
      const params = { limit, offset: nextOffset };
      if (styleFilter !== "all") params.style = styleFilter;
      if (subjectFilter !== "all") params.subject = subjectFilter;
      if (search.trim()) params.q = search.trim();
      const r = await api.get("/image-library", { params });
      setGated(null);
      setItems(nextOffset === 0 ? (r.data.items || []) : (prev) => [...prev, ...(r.data.items || [])]);
      setTotal(r.data.total || 0);
      setOffset(nextOffset);
    } catch (err) {
      if (err.response?.status === 402) {
        setGated(err.response.data?.detail || { plan: "free", reason: "The Image Library is a Plus/Pro feature." });
        setItems([]);
      }
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleFilter, subjectFilter, search]);

  useEffect(() => {
    // Subjects list is also gated — best-effort fetch, ignore 402.
    api.get("/image-library/subjects").then((r) => setSubjects(r.data.subjects || [])).catch(() => {});
  }, []);

  useEffect(() => { load(0); /* eslint-disable-next-line */ }, [styleFilter, subjectFilter]);

  const submitSearch = (e) => { e.preventDefault(); load(0); };

  // ---- Free-user paywall ------------------------------------------------
  if (gated) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="library-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: "var(--primary)" }}>
            <LibraryIcon size={18} color="white" />
          </div>
          <h1 className="font-display text-4xl font-bold">Image Library</h1>
        </div>
        <div className="pm-card p-8 text-center" data-testid="library-paywall">
          <Crown size={32} className="mx-auto mb-3" style={{ color: "var(--accent)" }} />
          <h2 className="font-display text-2xl font-bold mb-2">Upgrade to browse the gallery</h2>
          <p className="text-base mb-5" style={{ color: "var(--text-secondary)" }}>
            {gated.reason || "The Image Library is a Plus/Pro feature."} Every AI illustration ever rendered on PictureMed lives here — searchable by subject, topic, and style.
          </p>
          <Link to="/app/plans" className="pm-btn-primary inline-flex items-center gap-2" data-testid="library-upgrade-cta">
            <Crown size={16} /> See Plus & Pro plans
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="library-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: "var(--primary)" }}>
          <LibraryIcon size={18} color="white" />
        </div>
        <h1 className="font-display text-4xl font-bold">Image Library</h1>
      </div>
      <p className="text-lg mb-6" style={{ color: "var(--text-secondary)" }}>
        Every AI illustration ever rendered on PictureMed, searchable by subject, topic, or visual style.
      </p>

      {/* Search bar */}
      <form onSubmit={submitSearch} className="mb-4" data-testid="library-search-form">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by topic or subtopic name…"
              className="pm-input pl-10 w-full"
              data-testid="library-search-input"
            />
          </div>
          <button type="submit" className="pm-btn-primary text-sm" data-testid="library-search-btn">
            Search
          </button>
        </div>
      </form>

      {/* Style filter */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="library-style-filter">
        {["all", "editorial", "isometric", "watercolor", "retro-diagram"].map((s) => {
          const active = styleFilter === s;
          return (
            <button
              key={s}
              onClick={() => setStyleFilter(s)}
              className="rounded-full text-xs font-medium inline-flex items-center gap-1.5 px-3 py-1.5 transition-transform hover:-translate-y-0.5"
              style={{
                background: "var(--paper)",
                border: `1.5px solid ${active ? "var(--primary)" : "var(--border)"}`,
                color: active ? "var(--primary)" : "var(--text-secondary)",
                boxShadow: active ? "0 0 0 2px rgba(79,121,66,0.15)" : "none",
              }}
              data-testid={`library-filter-${s}`}
            >
              {s !== "all" && (
                <span
                  className="w-2.5 h-2.5 rounded-full inline-block"
                  style={{ background: STYLE_SWATCH[s] || "var(--muted)" }}
                />
              )}
              {s === "all" ? "All styles" : STYLE_LABEL[s]}
            </button>
          );
        })}
      </div>

      {/* Subject filter */}
      {subjects.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="library-subject-filter">
          <button
            onClick={() => setSubjectFilter("all")}
            className="rounded-full text-xs font-medium px-3 py-1.5"
            style={{
              background: "var(--paper)",
              border: `1.5px solid ${subjectFilter === "all" ? "var(--primary)" : "var(--border)"}`,
              color: subjectFilter === "all" ? "var(--primary)" : "var(--text-secondary)",
            }}
            data-testid="library-subject-all"
          >
            All subjects
          </button>
          {subjects.map((s) => (
            <button
              key={s}
              onClick={() => setSubjectFilter(s)}
              className="rounded-full text-xs font-medium px-3 py-1.5 capitalize"
              style={{
                background: "var(--paper)",
                border: `1.5px solid ${subjectFilter === s ? "var(--primary)" : "var(--border)"}`,
                color: subjectFilter === s ? "var(--primary)" : "var(--text-secondary)",
              }}
              data-testid={`library-subject-${s}`}
            >
              {s.replace(/-/g, " ")}
            </button>
          ))}
        </div>
      )}

      {/* Grid */}
      {loading && items.length === 0 ? (
        <div className="text-center py-16" data-testid="library-loading">
          <Loader2 className="animate-spin mx-auto mb-3" size={28} style={{ color: "var(--primary)" }} />
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading library…</p>
        </div>
      ) : items.length === 0 ? (
        <div className="pm-card p-10 text-center" data-testid="library-empty">
          <Palette size={32} className="mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
          <p className="text-lg font-display font-bold mb-1">No images match this filter</p>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            Try a different subject, style, or search term.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="library-grid">
            {items.map((it) => (
              <button
                key={it.id}
                onClick={() => setPreview(it)}
                className="pm-card overflow-hidden text-left p-0 transition-transform hover:-translate-y-1"
                data-testid={`library-item-${it.id}`}
              >
                <div className="aspect-square overflow-hidden" style={{ background: "var(--muted)" }}>
                  <img src={it.image_url} alt={it.topic_name || "PictureMed illustration"} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span
                      className="text-[10px] font-medium inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                      style={{ background: "var(--paper)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}
                    >
                      <span className="w-2 h-2 rounded-full inline-block" style={{ background: STYLE_SWATCH[it.style] || "var(--muted)" }} />
                      {STYLE_LABEL[it.style] || it.style}
                    </span>
                  </div>
                  <p className="text-xs font-semibold line-clamp-1" style={{ color: "var(--text-primary)" }}>
                    {it.topic_name || "Untitled"}
                  </p>
                  {it.subtopic_name && (
                    <p className="text-[11px] line-clamp-1" style={{ color: "var(--text-muted)" }}>
                      {it.subtopic_name}
                    </p>
                  )}
                  {it.subject_id && (
                    <p className="text-[10px] mt-1 capitalize" style={{ color: "var(--text-muted)" }}>
                      {it.subject_id.replace(/-/g, " ")}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>

          {items.length < total && (
            <div className="text-center mt-6">
              <button
                onClick={() => load(offset + limit)}
                disabled={loading}
                className="pm-btn-secondary"
                data-testid="library-load-more"
              >
                {loading ? "Loading…" : `Load more (${total - items.length} remaining)`}
              </button>
            </div>
          )}
        </>
      )}

      {/* Preview modal — never shows raw prompts or reuse counts */}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
          onClick={() => setPreview(null)}
          data-testid="library-preview-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="pm-card p-4 max-w-2xl w-full relative"
          >
            <button
              onClick={() => setPreview(null)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: "var(--muted)" }}
              data-testid="library-preview-close"
            >
              <X size={16} />
            </button>
            <img src={preview.image_url} alt={preview.topic_name || "Illustration"} className="w-full rounded-xl mb-3" />
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span
                className="text-xs font-medium inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                style={{ background: "var(--paper)", border: "1px solid var(--border)" }}
              >
                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: STYLE_SWATCH[preview.style] || "var(--muted)" }} />
                {STYLE_LABEL[preview.style] || preview.style}
              </span>
              {preview.subject_id && (
                <span className="text-xs capitalize" style={{ color: "var(--text-muted)" }}>
                  {preview.subject_id.replace(/-/g, " ")}
                </span>
              )}
            </div>
            <h3 className="font-display text-xl font-bold mb-1" data-testid="library-preview-title">
              {preview.topic_name || "Untitled illustration"}
            </h3>
            {preview.analogy_text && (
              <p className="text-sm leading-relaxed mb-3" style={{ color: "var(--text-secondary)" }} data-testid="library-preview-analogy">
                {preview.analogy_text}
              </p>
            )}
            {preview.topic_id ? (
              <div className="mt-4 pt-3 border-t flex flex-col gap-2" style={{ borderColor: "var(--border)" }}>
                <div className="flex items-start gap-2 text-xs" style={{ color: "var(--text-secondary)" }}>
                  <Sparkles size={12} className="mt-0.5 flex-shrink-0" style={{ color: "var(--primary)" }} />
                  <span>
                    <b style={{ color: "var(--text)" }}>Already generated —</b> opening this topic won&apos;t cost credits. A fresh AI render only costs when you tap <b>Regenerate</b> on the topic page.
                  </span>
                </div>
                <Link
                  to={`/app/topic/${preview.topic_id}`}
                  onClick={() => setPreview(null)}
                  className="pm-btn-primary text-sm inline-flex items-center justify-center gap-1.5"
                  data-testid="library-preview-open-topic"
                >
                  Open topic <ArrowRight size={14} />
                </Link>
              </div>
            ) : (
              <p className="text-xs italic mt-3" style={{ color: "var(--text-muted)" }}>
                This illustration isn&apos;t tied to a specific topic yet.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
